import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import AdminDeletePostButton from '@/components/AdminDeletePostButton'
import PostComments from '@/components/PostComments'
import { isHttpImageUrl } from '@/lib/tokenImage'
import type { PostCommentKind } from '@/lib/postComments'

export default function PostThread({
  postKind,
  postId,
  image,
  accent = 'lime',
  imageAlt = 'Post photo',
}: {
  postKind: PostCommentKind
  postId: string
  image?: string
  accent?: 'lime' | 'orange'
  imageAlt?: string
}) {
  const [open, setOpen] = useState(false)
  const hasImage = Boolean(image && isHttpImageUrl(image))

  useEffect(() => {
    if (!open)
      return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
        setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      {hasImage
        ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-2 block w-full overflow-hidden rounded-2xl text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60"
              aria-label="Open photo and comments"
            >
              <img src={image} alt={imageAlt} className="h-auto max-h-96 w-full object-contain" />
            </button>
          )
        : null}

      <div className="mt-2 flex items-center justify-end">
        <AdminDeletePostButton postKind={postKind} postId={postId} />
      </div>
      <PostComments postKind={postKind} postId={postId} accent={accent} />

      {open && hasImage
        ? createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Photo comments"
              onClick={() => setOpen(false)}
            >
              <div
                className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-zinc-950 p-4"
                onClick={event => event.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-white">Photo comments</p>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-full border border-white/15 px-3 py-1 text-xs text-zinc-300 hover:text-white"
                  >
                    Close
                  </button>
                </div>
                <img src={image} alt={imageAlt} className="mt-3 h-auto max-h-[46vh] w-full rounded-2xl object-contain" />
                <PostComments postKind={postKind} postId={postId} accent={accent} />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
